/**
 * Mock Market Data Generator
 *
 * Generates realistic-looking historical price data for the Model Tab UI.
 * Will be replaced with real data from Polygon.io/Massive in Phase 4.
 */

export interface PricePoint {
  date: string;
  value: number;
}

export interface ComparisonData {
  userPortfolio: PricePoint[];
  benchmark: PricePoint[];
  tacticalFund: PricePoint[];
}

// Fund definitions grouped by provider
export const FUND_OPTIONS = {
  'Tactical Management': [
    { ticker: 'TACTICAL', name: 'Tactical Allocation Fund', description: 'Active risk management with cash conversion' },
  ],
  'Vanguard': [
    { ticker: 'VTI', name: 'Total Stock Market ETF', description: 'Broad US equity exposure' },
    { ticker: 'VXUS', name: 'Total International Stock ETF', description: 'International equity exposure' },
    { ticker: 'BND', name: 'Total Bond Market ETF', description: 'Investment-grade bonds' },
    { ticker: 'VT', name: 'Total World Stock ETF', description: 'Global equity exposure' },
  ],
  'Fidelity': [
    { ticker: 'FXAIX', name: '500 Index Fund', description: 'S&P 500 index tracking' },
    { ticker: 'FTIHX', name: 'Total International Index', description: 'International equity index' },
    { ticker: 'FXNAX', name: 'US Bond Index', description: 'US investment-grade bonds' },
  ],
  'Schwab': [
    { ticker: 'SCHX', name: 'US Large-Cap ETF', description: 'Large-cap US equities' },
    { ticker: 'SCHF', name: 'International Equity ETF', description: 'Developed market equities' },
    { ticker: 'SCHZ', name: 'US Aggregate Bond ETF', description: 'US investment-grade bonds' },
  ],
  'iShares': [
    { ticker: 'IVV', name: 'Core S&P 500 ETF', description: 'S&P 500 index tracking' },
    { ticker: 'IEFA', name: 'Core MSCI EAFE ETF', description: 'Developed market equities' },
    { ticker: 'AGG', name: 'Core US Aggregate Bond ETF', description: 'US investment-grade bonds' },
  ],
} as const;

export type FundProvider = keyof typeof FUND_OPTIONS;

// Historical market events for realistic simulation
const MARKET_EVENTS = [
  { year: 2008, month: 9, drawdown: -0.38, name: '2008 Financial Crisis' },
  { year: 2011, month: 8, drawdown: -0.16, name: '2011 Debt Ceiling Crisis' },
  { year: 2015, month: 8, drawdown: -0.11, name: '2015 China Slowdown' },
  { year: 2018, month: 12, drawdown: -0.14, name: '2018 Q4 Correction' },
  { year: 2020, month: 3, drawdown: -0.34, name: '2020 COVID Crash' },
  { year: 2022, month: 6, drawdown: -0.23, name: '2022 Bear Market' },
];

/**
 * Generate a random walk with drift (simulating stock returns)
 */
function generateRandomWalk(
  startValue: number,
  days: number,
  annualReturn: number,
  annualVolatility: number,
  seed?: number
): number[] {
  const dailyReturn = annualReturn / 252;
  const dailyVolatility = annualVolatility / Math.sqrt(252);

  const values: number[] = [startValue];
  let currentValue = startValue;

  // Simple seeded random for reproducibility
  let randomState = seed ?? Date.now();
  const seededRandom = () => {
    randomState = (randomState * 1103515245 + 12345) & 0x7fffffff;
    return randomState / 0x7fffffff;
  };

  for (let i = 1; i < days; i++) {
    // Box-Muller transform for normal distribution
    const u1 = seededRandom();
    const u2 = seededRandom();
    const z = Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);

    const dailyChange = dailyReturn + dailyVolatility * z;
    currentValue *= (1 + dailyChange);
    values.push(currentValue);
  }

  return values;
}

/**
 * Apply historical market events to a price series
 */
function applyMarketEvents(
  values: number[],
  startDate: Date,
  isTactical: boolean = false
): number[] {
  const result = [...values];

  MARKET_EVENTS.forEach(event => {
    const eventDate = new Date(event.year, event.month - 1, 15);
    const daysSinceStart = Math.floor((eventDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));

    if (daysSinceStart > 0 && daysSinceStart < values.length - 60) {
      // Apply drawdown over ~30 days
      const drawdownDays = 30;
      const recoveryDays = 60;

      // Tactical funds have reduced drawdown (cash conversion)
      const effectiveDrawdown = isTactical ? event.drawdown * 0.4 : event.drawdown;

      for (let i = 0; i < drawdownDays && daysSinceStart + i < result.length; i++) {
        const progress = i / drawdownDays;
        const drawdownFactor = 1 + (effectiveDrawdown * Math.sin(progress * Math.PI / 2));
        result[daysSinceStart + i] *= drawdownFactor;
      }

      // Gradual recovery
      for (let i = drawdownDays; i < drawdownDays + recoveryDays && daysSinceStart + i < result.length; i++) {
        const progress = (i - drawdownDays) / recoveryDays;
        const recoveryFactor = 1 + (Math.abs(effectiveDrawdown) * 0.5 * progress);
        result[daysSinceStart + i] *= recoveryFactor;
      }
    }
  });

  return result;
}

/**
 * Generate dates array spanning the full calendar period
 * Returns roughly 252 points per year (trading days) spread across actual calendar dates
 */
function generateDates(startDate: Date, endDate: Date, pointsPerYear: number = 252): string[] {
  const dates: string[] = [];
  const totalMs = endDate.getTime() - startDate.getTime();
  const years = totalMs / (365.25 * 24 * 60 * 60 * 1000);
  const totalPoints = Math.round(years * pointsPerYear);
  const msPerPoint = totalMs / totalPoints;

  for (let i = 0; i <= totalPoints; i++) {
    const date = new Date(startDate.getTime() + i * msPerPoint);
    dates.push(date.toISOString().split('T')[0]);
  }

  return dates;
}

/**
 * Generate mock historical comparison data
 */
export function generateMockComparisonData(
  years: number,
  portfolioValue: number = 100000,
  selectedFund: string = 'TACTICAL',
  userExpectedReturn: number = 0.07, // Actual portfolio expected return
  userVolatility: number = 0.15 // Actual portfolio volatility
): ComparisonData {
  const endDate = new Date();
  const startDate = new Date();
  startDate.setFullYear(startDate.getFullYear() - years);

  const dates = generateDates(startDate, endDate);
  const days = dates.length;

  // Use years as seed modifier so different time ranges show different patterns
  const yearSeed = years * 1000;

  // User portfolio: use actual portfolio return and volatility characteristics
  const userReturns = generateRandomWalk(portfolioValue, days, userExpectedReturn, userVolatility, 12345 + yearSeed);
  const userWithEvents = applyMarketEvents(userReturns, startDate, false);

  // S&P 500 benchmark: historical-like returns
  const benchmarkReturns = generateRandomWalk(portfolioValue, days, 0.10, 0.16, 54321 + yearSeed);
  const benchmarkWithEvents = applyMarketEvents(benchmarkReturns, startDate, false);

  // Tactical fund: similar upside, reduced downside
  const isTactical = selectedFund === 'TACTICAL';
  const tacticalReturns = generateRandomWalk(portfolioValue, days, isTactical ? 0.09 : 0.08, isTactical ? 0.12 : 0.15, 98765 + yearSeed);
  const tacticalWithEvents = applyMarketEvents(tacticalReturns, startDate, isTactical);

  return {
    userPortfolio: dates.map((date, i) => ({ date, value: userWithEvents[i] })),
    benchmark: dates.map((date, i) => ({ date, value: benchmarkWithEvents[i] })),
    tacticalFund: dates.map((date, i) => ({ date, value: tacticalWithEvents[i] })),
  };
}

/**
 * Generate forecast data with scenarios
 */
export function generateForecastData(
  years: number,
  currentValue: number,
  _scenario: 'bull' | 'base' | 'bear',
  stressLevel: number = 0, // 0 to -50
  userExpectedReturn: number = 0.07, // Actual portfolio expected return
  userVolatility: number = 0.15 // Actual portfolio volatility
): ComparisonData {
  const startDate = new Date();
  const endDate = new Date();
  endDate.setFullYear(endDate.getFullYear() + years);

  const dates = generateDates(startDate, endDate);
  const days = dates.length;

  // Adjust returns based on stress level - more negative stress = lower returns
  const stressFactor = stressLevel / 100; // Convert to decimal (-0.5 to 0)

  // Base returns adjusted by stress level - user portfolio uses actual characteristics
  const baseReturns = {
    user: userExpectedReturn + stressFactor * 0.1,      // Use actual return, drops with stress
    benchmark: 0.10 + stressFactor * 0.12, // 10% base, drops with stress
    tactical: 0.09 + stressFactor * 0.05,  // 9% base, tactical is more resilient
  };

  // Volatility increases with stress - user portfolio uses actual volatility
  const baseVolatility = {
    user: userVolatility - stressFactor * 0.1,      // Use actual volatility, increases with stress
    benchmark: 0.16 - stressFactor * 0.08,
    tactical: 0.12 - stressFactor * 0.04, // Tactical has lower vol increase
  };

  // Use stress level and years as part of seed to get different paths for different settings
  const stressSeed = Math.abs(Math.round(stressLevel * 1000));
  const yearSeed = years * 1000;

  const userReturns = generateRandomWalk(currentValue, days, baseReturns.user, baseVolatility.user, 11111 + stressSeed + yearSeed);
  const benchmarkReturns = generateRandomWalk(currentValue, days, baseReturns.benchmark, baseVolatility.benchmark, 22222 + stressSeed + yearSeed);
  const tacticalReturns = generateRandomWalk(currentValue, days, baseReturns.tactical, baseVolatility.tactical, 33333 + stressSeed + yearSeed);

  // Apply initial drawdown shock if stress level is significant (< -10%)
  const applyStressShock = (values: number[], isTactical: boolean) => {
    if (stressFactor > -0.10) return values; // No significant stress shock

    const result = [...values];
    // Tactical funds have reduced drawdown due to risk management
    const effectiveStress = isTactical ? stressFactor * 0.4 : stressFactor;
    const shockDays = Math.min(90, days); // 90 day shock period

    for (let i = 0; i < shockDays; i++) {
      const progress = i / shockDays;
      // Sharp drop then gradual recovery
      const shockCurve = Math.sin(progress * Math.PI) * (1 - progress * 0.5);
      const shockImpact = effectiveStress * shockCurve;
      result[i] *= (1 + shockImpact);
    }

    return result;
  };

  const userWithShock = applyStressShock(userReturns, false);
  const benchmarkWithShock = applyStressShock(benchmarkReturns, false);
  const tacticalWithShock = applyStressShock(tacticalReturns, true);

  return {
    userPortfolio: dates.map((date, i) => ({ date, value: userWithShock[i] })),
    benchmark: dates.map((date, i) => ({ date, value: benchmarkWithShock[i] })),
    tacticalFund: dates.map((date, i) => ({ date, value: tacticalWithShock[i] })),
  };
}

/**
 * Sample down data for chart display (reduce points for performance)
 */
export function sampleData(data: PricePoint[], targetPoints: number = 250): PricePoint[] {
  if (data.length <= targetPoints) return data;

  const step = Math.floor(data.length / targetPoints);
  return data.filter((_, i) => i % step === 0 || i === data.length - 1);
}

/**
 * Calculate performance metrics from price data
 */
export function calculateMetrics(data: PricePoint[]): {
  totalReturn: number;
  cagr: number;
  maxDrawdown: number;
  finalValue: number;
} {
  if (data.length < 2) {
    return { totalReturn: 0, cagr: 0, maxDrawdown: 0, finalValue: 0 };
  }

  const startValue = data[0].value;
  const endValue = data[data.length - 1].value;
  const years = data.length / 252;

  const totalReturn = (endValue - startValue) / startValue;
  const cagr = Math.pow(endValue / startValue, 1 / years) - 1;

  // Calculate max drawdown
  let peak = startValue;
  let maxDrawdown = 0;

  data.forEach(point => {
    if (point.value > peak) peak = point.value;
    const drawdown = (peak - point.value) / peak;
    if (drawdown > maxDrawdown) maxDrawdown = drawdown;
  });

  return {
    totalReturn,
    cagr,
    maxDrawdown,
    finalValue: endValue,
  };
}
